import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@touchtech/web-baselayer-ui";

import AttributeCarousel from "@is/components/AttributeCarousel/AttributeCarousel";
import PriceRangeSlider from "@is/components/PriceRangeSlider";
import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { PreferenceOption, PriceRange } from "@is/types";

import AttributeOptions from "./AttributeOptions";
import css from "./AttributePanel.module.css";
import useManageAttributePanel from "./hooks/useManageAttributePanel";

type Props = {
  preferences: PreferenceOption[];
  onPreferenceClick: (preference: PreferenceOption) => void;
  onPriceRangeSearch: (range: PriceRange) => void;
  arePreferencesPending: boolean;
  drawerRef?: React.RefObject<HTMLDivElement>;
  onSearchDrawerHeightSet: (height: number) => void;
  currencyCode: string;
};

const AttributePanel = ({
  preferences,
  onPreferenceClick,
  onPriceRangeSearch,
  arePreferencesPending,
  drawerRef,
  onSearchDrawerHeightSet,
  currencyCode,
}: Props) => {
  const {
    areOptionsExpanded,
    setAreOptionsExpanded,
    tabOptions,
    optionsSelectedFromTab,
    isSelected,
    sliderAdditionalOptions,
    AttributeCarouselContent,
    isPriceTabSelected,
    actualMinPrice,
    actualMaxPrice,
    shouldShowNoOptionsForPrice,
    handlePriceRangeChange,
  } = useManageAttributePanel({
    preferences,
    drawerRef,
    onSearchDrawerHeightSet,
    currencyCode,
  });

  const { priceRange } = useContext(ViewStateContext).content;
  const { t } = useTranslation(TranslationContext.ProductListing);

  // Render price tab content (message or slider)
  const renderPriceTabContent = () => {
    if (shouldShowNoOptionsForPrice) {
      return (
        <div className={css.noOptions}>
          <span className="text-label-lg">
            {t("No options that match your selections")}
          </span>
        </div>
      );
    }

    return (
      <PriceRangeSlider
        minValue={actualMinPrice}
        maxValue={actualMaxPrice}
        value={priceRange}
        currencyCode={currencyCode}
        onChange={handlePriceRangeChange}
        onRelease={onPriceRangeSearch}
      />
    );
  };

  // Render main content based on selected tab
  const renderMainContent = () => {
    if (isPriceTabSelected) {
      return renderPriceTabContent();
    }

    return (
      <AttributeOptions
        preferences={preferences}
        onPreferenceClick={onPreferenceClick}
        areOptionsExpanded={areOptionsExpanded}
        setAreOptionsExpanded={setAreOptionsExpanded}
        tabOptions={tabOptions}
        optionsSelectedFromTab={optionsSelectedFromTab}
        isSelected={isSelected}
      />
    );
  };

  return (
    <div className={css.container}>
      <div className={css.tabsContainer}>
        <AttributeCarousel
          additionalOptions={sliderAdditionalOptions}
          content={AttributeCarouselContent}
        />
      </div>
      {arePreferencesPending ? (
        <div className={css.spinnerContainer}>
          <Spinner className="text-headline-5xl" />
        </div>
      ) : (
        renderMainContent()
      )}
    </div>
  );
};

export default AttributePanel;
